import {
  LOAD_MORE_SCROLL_THRESHOLD,
  SUPPORT_MESSAGES_PAGE_SIZE,
} from '@features/SupportChat/constants';
import { useSupportChatAttachments } from '@features/SupportChat/hooks/useSupportChatAttachments';
import { useSupportChatFileDownload } from '@features/SupportChat/hooks/useSupportChatFileDownload';
import { useSupportChatUnread } from '@features/SupportChat/hooks/useSupportChatUnread';
import { mapSupportMessages } from '@features/SupportChat/utils/mapSupportMessages';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  useGetSupportMessagesQuery,
  useSendMessageToSupportMutation,
} from '@app/api/Support';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useErrorHandler } from '@app/shared/hooks';

import type { UseSupportChatResult } from '@features/SupportChat/types';
import type { UIEvent } from 'react';

export const useSupportChat = (isOpen: boolean): UseSupportChatResult => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const { showNotification } = useNotification();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Расстояние от низа ленты до текущей позиции перед догрузкой истории:
  // после добавления старых сообщений сверху восстанавливаем его, чтобы
  // лента не прыгала.
  const distanceFromBottomRef = useRef<number | null>(null);
  const lastMessageIdRef = useRef<number | null>(null);

  const [countMessages, setCountMessages] = useState(
    SUPPORT_MESSAGES_PAGE_SIZE
  );
  const [inputValue, setInputValue] = useState('');

  const { attachments, clearAttachments, ...attachmentsModel } =
    useSupportChatAttachments();
  const fileDownloadModel = useSupportChatFileDownload();
  const unreadModel = useSupportChatUnread();

  const isUnavailable = ActiveBranchId == null;

  const {
    data: chat,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetSupportMessagesQuery(
    { ActiveBranchId: ActiveBranchId!, countMessages },
    // Пока окно закрыто, OnSupportMessage не слушаем (см. onCacheEntryAdded),
    // поэтому при каждом открытии перечитываем переписку
    { skip: !isOpen || isUnavailable, refetchOnMountOrArgChange: true }
  );

  useErrorHandler([{ flag: isError, error }]);

  const [sendMessageToSupport, { isLoading: isSending }] =
    useSendMessageToSupportMutation();

  const messages = useMemo(() => mapSupportMessages(chat), [chat]);

  const hasMore = (chat?.TotalMessages ?? 0) > messages.length;
  const isLoadingMore = isFetching && distanceFromBottomRef.current !== null;

  // Держит ленту на месте: после догрузки истории восстанавливает позицию,
  // при новом последнем сообщении прокручивает вниз.
  const syncMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;

    if (!isOpen || !container) return;

    if (distanceFromBottomRef.current !== null) {
      if (isFetching) return;

      container.scrollTop =
        container.scrollHeight - distanceFromBottomRef.current;
      distanceFromBottomRef.current = null;

      return;
    }

    const lastMessageId = messages.at(-1)?.id ?? null;

    if (lastMessageId !== lastMessageIdRef.current) {
      lastMessageIdRef.current = lastMessageId;
      container.scrollTop = container.scrollHeight;
    }
  }, [isFetching, isOpen, messages]);

  // Сбрасывает догруженную историю и позицию, чтобы при следующем открытии
  // снова показать последние сообщения.
  const resetChatState = useCallback(() => {
    lastMessageIdRef.current = null;
    distanceFromBottomRef.current = null;
    setCountMessages(SUPPORT_MESSAGES_PAGE_SIZE);
  }, []);

  const handleMessagesScroll = useCallback(
    ({ currentTarget }: UIEvent<HTMLDivElement>) => {
      if (
        currentTarget.scrollTop > LOAD_MORE_SCROLL_THRESHOLD ||
        !hasMore ||
        isFetching
      ) {
        return;
      }

      distanceFromBottomRef.current =
        currentTarget.scrollHeight - currentTarget.scrollTop;
      setCountMessages((count) => count + SUPPORT_MESSAGES_PAGE_SIZE);
    },
    [hasMore, isFetching]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  const handleSend = useCallback(async () => {
    const message = inputValue.trim();
    const hasContent = Boolean(message) || attachments.length > 0;

    if (
      !hasContent ||
      isUnavailable ||
      isSending ||
      attachmentsModel.isReadingFiles
    ) {
      return;
    }

    try {
      await sendMessageToSupport({
        ActiveBranchId,
        data: {
          Message: message || null,
          Attachments: attachments.length
            ? attachments.map(({ name, data }) => ({ Name: name, Data: data }))
            : null,
        },
      }).unwrap();

      setInputValue('');
      clearAttachments();
    } catch (sendError) {
      showNotification(handleCatchError(sendError));
    }
  }, [
    ActiveBranchId,
    attachments,
    attachmentsModel.isReadingFiles,
    clearAttachments,
    inputValue,
    isSending,
    isUnavailable,
    sendMessageToSupport,
    showNotification,
  ]);

  return {
    messages,
    inputValue,
    isLoading,
    isLoadingMore,
    isSending,
    isUnavailable,
    messagesContainerRef,
    handleInputChange,
    handleSend,
    handleMessagesScroll,
    attachments,
    ...attachmentsModel,
    ...fileDownloadModel,
    ...unreadModel,
    syncMessagesScroll,
    resetChatState,
  };
};
